class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // The theme also puts is-empty on <cart-drawer> itself; Dawn only clears it from
    // .drawer__inner, which left the drawer in its empty state after an AJAX add.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// LL cart upsell — add single variant to cart then re-render drawer
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.ll-cart-upsell__add');
  if (!btn) return;
  event.preventDefault();
  const variantId = parseInt(btn.dataset.variantId, 10);
  if (!variantId) return;
  btn.disabled = true;
  btn.textContent = '…';
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    }),
  })
    .then((r) => r.json())
    .then((state) => {
      const drawer = document.querySelector('cart-drawer');
      // The re-rendered drawer carries the correct bar width from Liquid; the
      // observer below picks it up. No extra /cart.js round trip needed.
      if (drawer) drawer.renderContents(state);
    })
    .catch(() => {
      btn.disabled = false;
      btn.textContent = '+ add';
    });
});

// ── LL free-shipping progress bar ────────────────────────────
//
// The width and the label are both rendered by snippets/ll-cart-progress.liquid,
// server-side, so they are already correct whenever a render lands. This code does
// one job: make the fill SLIDE from the previous value to the new one instead of
// snapping, because every cart mutation replaces the bar with a brand-new node and
// a replaced node cannot transition on its own.
//
// It used to compute the width here from a /cart.js fetch. That only ran on
// DOMContentLoaded and on Dawn's cartUpdate event, so any path that re-rendered the
// drawer without publishing that event (an AJAX add, most notably) left a fresh
// 0%-width node on screen under a correct label.

let llPrevPct = null;
let llSeenBar = null;

function llSyncProgress() {
  const bar = document.querySelector('.ll-cart-progress');
  if (!bar) {
    llSeenBar = null;
    return;
  }
  // Same node as last time means we have already animated it — bail, or we would
  // cancel an in-flight transition every time the observer fires.
  if (bar === llSeenBar) return;
  llSeenBar = bar;

  const fill = bar.querySelector('.ll-cart-progress__fill');
  if (!fill) return;

  let target = parseFloat(bar.dataset.pct);
  if (!isFinite(target)) return;
  target = Math.max(0, Math.min(100, target));

  // First bar of the session: no previous value, so show the real width at once.
  const from = llPrevPct === null ? target : llPrevPct;
  llPrevPct = target;
  if (from === target) return;

  fill.style.width = from + '%';
  // Two frames: one to paint the start width, one to start the transition.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      fill.style.width = target + '%';
    });
  });
}

// Catches every re-render of the drawer, whoever triggered it — Dawn's quantity and
// remove handlers, the upsell button, the collection carousel, a future section.
// Nothing has to remember to call us.
function llWatchDrawer() {
  llSyncProgress();
  const host = document.getElementById('CartDrawer');
  if (!host) return;
  new MutationObserver(llSyncProgress).observe(host, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', llWatchDrawer);
} else {
  llWatchDrawer();
}
